import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { practiceCalls, type Practice } from '../api/practices.js';
import { practiceWords } from './practice-words.js';
import { WaitNote } from './wait-note.js';
import { BusyLabel } from './busy-label.js';

/**
 * One practice (board R5): Swings, Time, Type and Notes. The athlete can change
 * Type and notes (ruled 1 November); nobody deletes a practice, so streaks and
 * stars cannot shift. Clips join it in stage 4.
 */
export function PracticeDetail(props: {
  practiceId: string;
  /** "Noa · " for a parent or coach; nothing for the athlete. */
  whose: string;
  back: { to: string; label: string };
  editable: boolean;
}) {
  const getToken = useAccessToken();
  const [practice, setPractice] = useState<Practice | null>(null);
  const [failed, setFailed] = useState('');
  const [editing, setEditing] = useState(false);
  const [types, setTypes] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    practiceCalls
      .read(getToken, props.practiceId)
      .then((found) => live && setPractice(found))
      .catch((cause: unknown) => live && setFailed(cause instanceof Error ? cause.message : 'This did not load. Try again.'));
    return () => {
      live = false;
    };
  }, [getToken, props.practiceId]);

  const title = practice ? `${props.whose}${props.whose ? 'Practice ' : 'Practice · '}${practiceWords.day(practice.practice_date)}` : 'Practice';

  function edit() {
    if (!practice) return;
    setTypes(practice.types);
    setNotes(practice.notes);
    setEditing(true);
  }

  async function save() {
    if (!practice) return;
    setSaving(true);
    setFailed('');
    try {
      setPractice(await practiceCalls.update(getToken, practice.id, { types, notes: notes.trim() }));
      setEditing(false);
    } catch (cause) {
      setFailed(cause instanceof Error ? cause.message : 'That did not work. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to={props.back.to} aria-label={props.back.label}>
          ‹
        </Link>
        <div>
          <h2>{title}</h2>
        </div>
      </div>
      {!practice && !failed ? <WaitNote className="empty">Loading…</WaitNote> : null}
      {failed ? <p className="warn-note">{failed}</p> : null}
      {practice ? (
        <>
          <div className="practice-figures practice-figures-3">
            <div>
              <span className="practice-label">Swings</span>
              <span className="practice-figure">{practice.swing_count}</span>
            </div>
            <div>
              <span className="practice-label">Time</span>
              <span className="practice-figure">{practiceWords.time(practice.time_seconds)}</span>
            </div>
            <div>
              <span className="practice-label">Type</span>
              <span className="practice-type-list">{practiceWords.typeList(practice.types) || '–'}</span>
            </div>
          </div>
          {editing ? (
            <>
              <span className="practice-label" id="practice-type">
                Type
              </span>
              <div className="practice-types" role="group" aria-labelledby="practice-type">
                {practiceWords.types.map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    aria-pressed={types.includes(code)}
                    onClick={() => setTypes((now) => (now.includes(code) ? now.filter((each) => each !== code) : [...now, code]))}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <label className="practice-label" htmlFor="practice-notes">
                Notes
              </label>
              <textarea id="practice-notes" className="practice-notes" maxLength={2000} value={notes} onChange={(event) => setNotes(event.target.value)} />
              <div className="actions">
                <button type="button" className="primary" onClick={() => void save()} disabled={saving} aria-busy={saving}>
                  <BusyLabel busy={saving} working="Saving…">
                    Save
                  </BusyLabel>
                </button>
                <button type="button" className="secondary" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="practice-label">Notes</span>
              <p className="practice-notes-text">{practice.notes || '–'}</p>
              {props.editable ? (
                <button type="button" className="secondary" onClick={edit}>
                  Edit
                </button>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </>
  );
}
